import { useState } from 'react';
import adminService from '@wlb/shared/api/admin/adminService';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../../components/ui/Button';
import { FormError } from '../../components/ui/FormError';
import { Modal } from '../../components/ui/Modal';
import { TextField } from '../../components/ui/TextField';

const MIN_PASSWORD_LENGTH = 8;

interface TemporaryPasswordDialogProps {
  open: boolean;
  userGuid: string;
  onClose: () => void;
}

export function TemporaryPasswordDialog({
  open,
  userGuid,
  onClose,
}: TemporaryPasswordDialogProps) {
  const { t } = useTranslation('admin');
  const [password, setPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    setPassword('');
    setFieldError(null);
    setSubmitError(null);
    onClose();
  };

  const submit = async () => {
    if (!password) {
      setFieldError(t('users.temporaryPassword.required'));
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setFieldError(t('users.temporaryPassword.tooShort'));
      return;
    }
    setFieldError(null);
    setSubmitError(null);
    setSubmitting(true);
    try {
      await adminService.setTemporaryPassword(userGuid, password);
      ToastService.show({
        type: 'success',
        message: t('users.toasts.passwordSet'),
      });
      close();
    } catch (cause) {
      setSubmitError(getUserFacingErrorMessage(cause, t('common.errorTitle')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('users.temporaryPassword.title')}
      description={t('users.temporaryPassword.description')}
      onClose={close}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? t('common.saving') : t('users.temporaryPassword.submit')}
          </Button>
        </>
      }>
      <FormError variant="banner" message={submitError} />
      <TextField
        label={t('users.temporaryPassword.passwordLabel')}
        placeholder={t('users.temporaryPassword.passwordPlaceholder')}
        type="password"
        autoComplete="new-password"
        value={password}
        error={fieldError}
        onChange={event => setPassword(event.target.value)}
      />
    </Modal>
  );
}
